<template>
  <loader :is-loading="loading || isSubmitting" />
  <div v-if="!loading" class="flex flex-col space-y-5 bg-white p-8 shadow-xs">
    <div class="relative flex justify-between text-left">
      <div class="flex w-full items-center justify-between">
        <div class="flex max-w-1/2 items-center space-x-4">
          <back-button :route="backRoute" />
          <div class="truncate">
            <lf-h1 v-tooltip="activeCustomWebhook?.name">
              <template v-if="activeCustomWebhook?.name">
                {{
                  $t("CUSTOM_WEBHOOKS.EDIT_TITLE", {
                    name: activeCustomWebhook.name,
                  })
                }}
              </template>
              <template v-else>
                {{ $t("CUSTOM_WEBHOOKS.CREATE_TITLE") }}
              </template>
              <span
                v-if="isLendflowUser"
                data-cy="client-name"
                class="block text-sm font-normal"
              >
                {{ activeCustomWebhook?.client?.name || "" }}
              </span>
            </lf-h1>
          </div>
        </div>
        <div class="flex items-center space-x-4">
          <template-action-buttons
            class="flex items-stretch space-x-4"
            form="customWebhookForm"
            :is-edit="isEdit"
            :is-submitting="isSubmitting"
            is-published
            :show-draft="false"
            data-cy="template-action-buttons"
            @delete="showDeleteModal = true"
            @publish="saveWebhook"
          >
            <template v-if="lastUpdated" #lastUpdated>
              {{ lastUpdated }}
            </template>
            <template #publishText>{{ $t("COMMON.SAVE") }}</template>
          </template-action-buttons>
        </div>
      </div>
    </div>
  </div>

  <view-wrapper class="px-20 xl:px-50">
    <form v-if="activeCustomWebhook" id="customWebhookForm" autocomplete="off">
      <lf-card has-padding class="mb-8 space-y-4 py-6">
        <lf-h3>
          {{ $t("CUSTOM_WEBHOOKS.DETAIL") }}
        </lf-h3>
        <lf-input
          data-cy="custom-webhook-name"
          name="name"
          :placeholder="t('CUSTOM_WEBHOOKS.NAME')"
          :value="activeCustomWebhook?.name"
          immediate
          @key-released="activeCustomWebhook.name = $event"
        />
        <div
          v-for="(webhookUrl, index) in activeCustomWebhook?.urls"
          :key="index"
          class="flex items-center space-x-2"
        >
          <lf-input
            :data-cy="`custom-webhook-url-${index}`"
            :name="`urls-${index}`"
            :placeholder="t('COMMON.URL')"
            :value="webhookUrl"
            class="url-input-wrapper"
            no-margin
            immediate
            @key-released="updateWebhookUrl($event, index)"
          >
            <template #suffix>
              <clear-button
                v-if="index > 0"
                :data-cy="`clear-url-button-${index}`"
                class="clear-link absolute -top-5-5 -left-1 text-gray-500 opacity-0"
                @click.prevent="removeUrl(index)"
              />
            </template>
          </lf-input>
        </div>
        <div class="flex justify-end">
          <color-button data-cy="add-url" @click.prevent="addUrl">
            {{ $t("COMMON.ADD") }} {{ $t("COMMON.URL") }}
          </color-button>
        </div>
        <lf-textarea
          data-cy="custom-webhook-description"
          name="description"
          placeholder="Description"
          :value="activeCustomWebhook?.description || ''"
          :character-limit="TEXT_CHARACTER_LIMIT_DEFAULT"
          noresize
          rows="2"
          immediate
          @key-released="activeCustomWebhook.description = $event"
        />
        <webhook-custom-notification-parameter
          v-for="(parameter, parameterIndex) in activeCustomWebhook.parameters"
          :key="parameterIndex"
          :parameter="parameter"
          :name="`parameter-${parameterIndex}`"
          :data-cy="`parameter-${parameterIndex}`"
          @parameter:change="updateParameter(parameterIndex, $event)"
          @parameter:delete="deleteParameter(parameterIndex)"
        />
        <div class="flex justify-end">
          <color-button data-cy="add-parameter" @click.prevent="addParameter">
            {{ $t("COMMON.ADD") }} {{ $t("COMMON.PARAMETER") }}
          </color-button>
        </div>
      </lf-card>
    </form>
  </view-wrapper>

  <confirm-modal
    v-if="showDeleteModal"
    :title="`${$t('COMMON.DELETE')} ${$t('CUSTOM_WEBHOOKS.TITLE')}`"
    :description="
      $t('ORGANIZATION.COMMUNICATION_TEMPLATES.DELETE_DESCRIPTION', {
        name: activeCustomWebhook?.name ?? $t('CUSTOM_WEBHOOKS.TITLE'),
      })
    "
    :confirm-button="$t('COMMON.DELETE')"
    confirm-button-type="error"
    data-cy="delete-confirmation-modal"
    :close="() => (showDeleteModal = false)"
    @confirm="handleDelete"
  />
</template>

<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

import { storeToRefs } from "pinia";
import { useForm } from "vee-validate";

import { AnalyticsEvent } from "@/enums/analytics";
import { TEXT_CHARACTER_LIMIT_DEFAULT } from "@/helpers/constants";
import { DEFAULT_CUSTOM_WEBHOOK_PARAMETER } from "@/helpers/constants/workflow";
import { formatDateCustom } from "@/helpers/formatting";
import { callStoreAction } from "@/helpers/vee-validate";
import { useAuth } from "@/hooks/auth";
import { usePromiseWrapper } from "@/hooks/common";
import { useNotification } from "@/hooks/notifications";
import router from "@/router";
import {
  ROUTE_CUSTOM_WEBHOOKS,
  ROUTE_CUSTOM_WEBHOOKS_EDIT,
} from "@/router/routes";
import customWebhookApiService from "@/services/modules/customWebhook";
import { useAnalyticsStore } from "@/stores/analytics";
import { useCustomWebhookStore } from "@/stores/customWebhook";

import WebhookCustomNotificationParameter from "@/components/deals/workflow/notifications/WebhookCustomNotificationParameter.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import TemplateActionButtons from "@/components/ui/TemplateActionButtons.vue";
import BackButton from "@/components/ui/buttons/BackButton.vue";
import ClearButton from "@/components/ui/buttons/ClearButton.vue";
import ColorButton from "@/components/ui/buttons/ColorButton.vue";

import type { IClient } from "@/models/clients";
import type { CustomWebhook } from "@/models/customWebhook";
import type { WorkflowWebhookParameter } from "@/models/workflows";

const route = useRoute();
const { t } = useI18n();
const store = useCustomWebhookStore();
const { activeCustomWebhook } = storeToRefs(store);
const { getters } = useStore();
const { isLendflowUser } = useAuth();
const { trackEvent } = useAnalyticsStore();

const { showMessage } = useNotification();

const { fetchWrapper: getCustomWebhook, loading } = usePromiseWrapper(
  async () => {
    if (!isEdit.value) {
      store.activeCustomWebhook = { ...values };
      return;
    }
    const id = String(route.params.templateId);
    await store.getCustomWebhook(id);
  }
);

const backRoute = ref({
  name: ROUTE_CUSTOM_WEBHOOKS,
});
const showDeleteModal = ref(false);
const values = reactive<CustomWebhook>({
  id: "",
  created_at: "",
  updated_at: "",
  name: "",
  description: "",
  urls: [""],
  parameters: [],
});

const isEdit = computed(() => route.name === ROUTE_CUSTOM_WEBHOOKS_EDIT);
const lastUpdated = computed(() =>
  activeCustomWebhook.value?.updated_at
    ? `${t("LOGS.LAST_UPDATED")} ${formatDateCustom(
        activeCustomWebhook.value.updated_at,
        "yyyy-MM-dd HH:mm:ss"
      )}`
    : null
);
const activeClient = computed<IClient | null>(() => getters["clients/active"]);
const activeClientId = computed(
  () => activeClient.value?.id ?? (route.params.id as string)
);

const { fetchWrapper: handleDelete } = usePromiseWrapper(async () => {
  if (!isEdit.value || !route.params.templateId) {
    return;
  }
  await customWebhookApiService.deleteCustomWebhook(
    route.params.templateId.toString()
  );
  trackEvent(AnalyticsEvent.CustomWebhookDeleted, {
    webhook_id: route.params.templateId.toString(),
  });
  await router.push(backRoute.value);
});

const { handleSubmit, isSubmitting, setValues } = useForm<
  Partial<CustomWebhook>
>({
  initialValues: { ...values },
});

const updateWebhookUrl = (value: string, index: number) => {
  if (!activeCustomWebhook.value) {
    return;
  }
  activeCustomWebhook.value.urls[index] = value;
};

const removeUrl = (index: number) => {
  if (!activeCustomWebhook.value) {
    return;
  }
  activeCustomWebhook.value.urls.splice(index, 1);
};

const addUrl = () => {
  if (!activeCustomWebhook.value) {
    return;
  }
  activeCustomWebhook.value.urls.push("");
};

const addParameter = () => {
  if (!activeCustomWebhook.value) {
    return;
  }
  if (!activeCustomWebhook.value.parameters) {
    activeCustomWebhook.value.parameters = [];
  }
  activeCustomWebhook.value.parameters.push(DEFAULT_CUSTOM_WEBHOOK_PARAMETER);
};

const deleteParameter = (index: number) => {
  if (!activeCustomWebhook.value?.parameters) {
    return;
  }
  activeCustomWebhook.value.parameters.splice(index, 1);
};

const updateParameter = (
  index: number,
  parameter: WorkflowWebhookParameter
) => {
  if (!activeCustomWebhook.value?.parameters) {
    return;
  }
  activeCustomWebhook.value.parameters[index] = parameter;
};

const getUrls = (values: Record<string, string | unknown>) => {
  const urls: string[] = [];
  Object.keys(values as Record<string, string | unknown>).forEach((key) => {
    if (key.includes("urls-")) {
      urls.push(values[key] as string);
      delete values[key];
    }
  });
  values.urls = urls;
};

const saveWebhook = () =>
  handleSubmit(async (formValues, actions) => {
    getUrls(formValues);
    const payload: Partial<CustomWebhook> = {
      name: formValues.name,
      description: formValues.description,
      urls: formValues.urls,
      parameters: activeCustomWebhook.value?.parameters,
    };

    if (isEdit.value && activeCustomWebhook.value?.id) {
      payload.id = activeCustomWebhook.value.id;
    }

    if (activeClientId.value) {
      payload.client_id = activeClientId.value;
    }

    const actionName = isEdit.value
      ? "updateCustomWebhook"
      : "createCustomWebhook";

    try {
      await callStoreAction(
        { clientId: activeClientId.value, payload },
        actions,
        actionName,
        store
      );
      if (isEdit.value) {
        trackEvent(AnalyticsEvent.CustomWebhookUpdated, {
          webhook_id: activeCustomWebhook.value?.id,
        });
      } else {
        trackEvent(AnalyticsEvent.CustomWebhookCreated, {
          webhook_id: store.activeCustomWebhook?.id,
        });
      }
      if (store.activeCustomWebhook) {
        setValues({
          ...store.activeCustomWebhook,
        });
      }
    } catch (error) {
      if ((error as { message: string })?.message?.includes("422")) {
        return;
      }
      const message =
        (error as { message: string })?.message ?? t("COMMON.ERROR_OCCURRED");
      showMessage(message, "error");
    }
    if (!isEdit.value) {
      if (!store.activeCustomWebhook?.id) {
        return;
      }
      await router.push({
        name: ROUTE_CUSTOM_WEBHOOKS_EDIT,
        params: {
          templateId: store.activeCustomWebhook?.id,
        },
      });
    }
  })();

watch(
  activeCustomWebhook,
  (newWebhook) => {
    if (newWebhook) {
      setValues({
        ...newWebhook,
      });
    }
  },
  { immediate: true, deep: true }
);

onMounted(async () => {
  await getCustomWebhook();
});

onBeforeUnmount(() => {
  store.activeCustomWebhook = null;
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.url-input-wrapper:hover .clear-link {
  @apply opacity-100;
}
</style>
